/**
 * Shared vocabulary for window/door service requests.
 *
 * Status is derived and stored server-side (see
 * server/routes/api/WindowDoor/serviceRequests.js). Nothing here recomputes
 * it — this module only decides how it looks and sorts, so the dashboard, the
 * detail page and the homepage widgets can't drift from each other.
 */

export const STATUS = {
  SUBMITTED: "Submitted",
  CONTACTED: "Homeowner Contacted",
  QUOTED: "Job Quoted",
  PARTS: "Parts Ordered",
  BILLING: "Job Completed - Billing Required",
  COMPLETED: "Service Completed",
  TAKEN_OFF: "Taken Off",
  INACTIVE: "Inactive",
};

// Statuses that still need somebody to act. Anything else is parked or done.
export const OPEN_STATUSES = [
  STATUS.SUBMITTED,
  STATUS.CONTACTED,
  STATUS.QUOTED,
  STATUS.PARTS,
  STATUS.BILLING,
];

export const isOpen = (status) => OPEN_STATUSES.includes(status);

// Order the status rail renders in — worst-news-first, matching how the shop
// reads the board.
export const STATUS_RAIL = [
  STATUS.SUBMITTED,
  STATUS.CONTACTED,
  STATUS.INACTIVE,
  STATUS.QUOTED,
  STATUS.PARTS,
  STATUS.BILLING,
  STATUS.COMPLETED,
  STATUS.TAKEN_OFF,
];

// Tailwind accent per status. Kept as class strings (not hex) so every
// theme comes along for free. Chips are translucent tints so they read on
// Liquid Glass as well as on solid cards; `dot` is the solid swatch.
export const STATUS_STYLES = {
  [STATUS.SUBMITTED]: {
    bar: "border-l-sky-500",
    dot: "bg-sky-500",
    text: "text-sky-600 dark:text-sky-400",
    chip: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  },
  [STATUS.CONTACTED]: {
    bar: "border-l-indigo-500",
    dot: "bg-indigo-500",
    text: "text-indigo-600 dark:text-indigo-400",
    chip: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  },
  [STATUS.QUOTED]: {
    bar: "border-l-amber-500",
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
    chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
  [STATUS.PARTS]: {
    bar: "border-l-blue-600",
    dot: "bg-blue-600",
    text: "text-blue-600 dark:text-blue-400",
    chip: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  },
  [STATUS.BILLING]: {
    bar: "border-l-yellow-500",
    dot: "bg-yellow-500",
    text: "text-yellow-600 dark:text-yellow-400",
    chip: "bg-yellow-500/20 text-yellow-800 dark:text-yellow-300",
  },
  [STATUS.COMPLETED]: {
    bar: "border-l-emerald-500",
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
    chip: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  [STATUS.TAKEN_OFF]: {
    bar: "border-l-red-500",
    dot: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
    chip: "bg-red-500/15 text-red-700 dark:text-red-300",
  },
  [STATUS.INACTIVE]: {
    bar: "border-l-slate-400",
    dot: "bg-slate-400",
    text: "text-slate-500 dark:text-slate-400",
    chip: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
  },
};

export const statusStyle = (status) =>
  STATUS_STYLES[status] || STATUS_STYLES[STATUS.SUBMITTED];

export const MANUFACTURERS = [
  "Andersen",
  "Jeld-Wen",
  "Marvin",
  "Silverline",
  "Simonton",
  "ThermaTru",
];

export const CONTACT_METHOD_LABELS = {
  verbal: "Verbal Contact",
  voicemail: "Left Voicemail",
  not_occupied: "Not Occupied",
};

export const EMPTY_CHECKLIST = {
  homeowner_contacted: false,
  contact_method: null,
  job_quoted: false,
  parts_ordered: false,
  tech_service_completed: false,
  office_service_completed: false,
  order_number: "",
};

/**
 * What the customer column shows. An unoccupied home has no homeowner to
 * name, and the old app displayed "Home Not Occupied" there — people scan for
 * that phrase, so keep it.
 */
export const customerLabel = (request) => {
  if (request?.homeowner_name) return request.homeowner_name;
  return request?.home_occupied === false ? "Home Not Occupied" : "Unknown";
};

export const addressLabel = (request) =>
  [
    [request?.address, request?.lot ? `Lot ${request.lot}` : ""]
      .filter(Boolean)
      .join(" "),
    request?.city,
    request?.state,
  ]
    .filter(Boolean)
    .join(", ");

/** Whole days since submission — the "# of days active" column. */
export const daysActive = (request) => {
  if (!request?.entry_date) return 0;
  const submitted = new Date(request.entry_date);
  if (Number.isNaN(submitted.getTime())) return 0;
  const diff = Date.now() - submitted.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
};

/** Aging buckets for the "needs attention" highlight. */
export const agingLevel = (request) => {
  if (!isOpen(request?.status)) return "none";
  const days = daysActive(request);
  if (days >= 14) return "critical";
  if (days >= 7) return "warning";
  return "none";
};

/** Compact date for table cells: "08/21/26, 10:16 AM". */
export const shortDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
